import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import { type StoredDiscountFragment, formatPrice, formatReservationDate } from "@stepapp/core";
import { Block, Button, SvgIcon, Typography, palette, spacing } from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { AppStorage } from "../services/AppStorage";

type Props = StoredDiscountFragment & {
  onPress: (voucher: StoredDiscountFragment | null) => void;
  pickMode?: boolean;
  selectedCoupon?: string;
};

const CouponItem = ({
  onPress,
  pickMode,
  selectedCoupon,
  id,
  value,
  code,
  description,
  expiresAt,
}: Props) => {
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));
  const coupon = useMemo(
    () => ({ id, value, code, description, expiresAt }),
    [id, value, code, description, expiresAt],
  );

  const { t } = useTranslation();
  const date = useMemo(
    () => formatReservationDate(coupon?.expiresAt || "", "dd MMMM yyyy"),
    [coupon?.expiresAt],
  );

  const handleCouponPress = useCallback(() => {
    const params = selectedCoupon === coupon?.code ? null : coupon;
    onPress(params);
  }, [onPress, selectedCoupon, coupon]);

  const selected = pickMode && coupon?.code === selectedCoupon;

  const rightButtonComponnet = useCallback(() => {
    return (
      !selected && (
        <Block style={styles.addCouponIcon}>
          <SvgIcon color={palette.primary.accent} name="plus" size={32} />
        </Block>
      )
    );
  }, [selected]);

  return (
    <Block
      borderRadius={1}
      marginBottom={2}
      style={[styles.container, selected && styles.selected]}
    >
      <Block
        borderRadius={1}
        paddingBottom={pickMode ? 1 : 2}
        paddingHorizontal={3}
        paddingTop={2}
        style={styles.borderSpacing}
      >
        <Block alignCenter justifySpace row>
          <Block alignCenter row>
            <SvgIcon name="coupon" size={24} />
            <Typography style={styles.couponName} variant="newH4">
              {coupon?.code}
            </Typography>
          </Block>
          <Typography variant="newH4">{priceFormatter(coupon?.value)}</Typography>
        </Block>
        {coupon?.description && (
          <Block paddingTop={1.5}>
            <Typography variant="lightLabel">{coupon?.description}</Typography>
          </Block>
        )}
        <Block paddingTop={1.5}>
          <Typography type="secondary" variant="lightLabel">
            {t("screens.coupons.validTo", { date })}
          </Typography>
        </Block>

        {pickMode && (
          <Block alignStart justifySpace row paddingTop={2}>
            <Button
              label={
                selected ? t("screens.coupons.removeCoupons") : t("screens.coupons.addCoupons")
              }
              onPress={handleCouponPress}
              rightComponent={rightButtonComponnet}
              type={selected ? "secondary" : "primary"}
              variant="small"
            />
          </Block>
        )}
      </Block>
    </Block>
  );
};

export default React.memo(CouponItem);

const styles = StyleSheet.create({
  addCouponIcon: {
    backgroundColor: palette.common.white,
    borderRadius: 30,
    marginLeft: 10,
  },
  borderSpacing: {
    borderColor: palette.common.white,
    borderWidth: spacing.border * 2,
  },
  container: {
    backgroundColor: palette.secondary.main,
    borderColor: palette.common.white,
    borderWidth: spacing.border * 2,
  },
  couponName: {
    marginLeft: 8,
  },
  selected: {
    borderColor: palette.primary.accent,
  },
});
